import {
  HomeIcon,
  CreditCardIcon,
  BanknotesIcon,
  ClockIcon,
  DocumentChartBarIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  Cog6ToothIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline'
import type { Role } from '@/types'

export type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>

export interface NavItem {
  /** i18n key under the shared `ui` catalog, e.g. "nav.dashboard". */
  labelKey: string
  href: string
  icon: IconType
  /** Roles allowed to see this item. Omit = visible to everyone signed in. */
  roles?: Role[]
}

export interface NavGroup {
  headingKey?: string
  items: NavItem[]
}

// Primary operational nav + management nav. Role gating is enforced in Phase 2
// (server-side via Pundit); here it only controls visibility of the link.
// Labels are i18n keys, resolved in AppSidebar via useTranslations().
export const NAVIGATION: NavGroup[] = [
  {
    items: [
      { labelKey: 'nav.dashboard', href: '/dashboard', icon: HomeIcon },
      { labelKey: 'nav.payments', href: '/payments', icon: CreditCardIcon },
      { labelKey: 'nav.payouts', href: '/payouts', icon: BanknotesIcon },
      // Reports = on-demand exports. Viewers can't trigger exports, so it's hidden from them.
      { labelKey: 'nav.reports', href: '/exports', icon: DocumentChartBarIcon, roles: ['superadmin', 'admin', 'operator'] },
    ],
  },
  {
    headingKey: 'nav.management',
    items: [
      // Central oversight log of all write actions — admins/superadmins only
      // (row visibility is further scoped by actor role server-side).
      { labelKey: 'nav.activity', href: '/activity', icon: ClockIcon, roles: ['superadmin', 'admin'] },
      { labelKey: 'nav.merchants', href: '/merchants', icon: BuildingStorefrontIcon, roles: ['superadmin', 'admin'] },
      { labelKey: 'nav.users', href: '/users', icon: UsersIcon, roles: ['superadmin', 'admin'] },
      // In-app user guide — admins/super admins only.
      { labelKey: 'nav.guide', href: '/guide', icon: BookOpenIcon, roles: ['superadmin', 'admin'] },
      { labelKey: 'nav.settings', href: '/settings', icon: Cog6ToothIcon },
    ],
  },
]

/** Filter a nav group's items by the current user's role. */
export function visibleItems(items: NavItem[], role: Role | null): NavItem[] {
  return items.filter((item) => !item.roles || (role !== null && item.roles.includes(role)))
}
